import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import type { LiveAlert, LivePosition, WebSocketMessage } from '../types';
import { useAuth } from './AuthContext';

type PositionCallback = (position: LivePosition) => void;
type AlertCallback = (alert: LiveAlert) => void;

interface WebSocketContextValue {
  subscribePosition: (deviceId: number, cb: PositionCallback) => void;
  unsubscribePosition: (deviceId: number) => void;
  subscribeAlerts: (cb: AlertCallback) => void;
  unsubscribeAlerts: () => void;
  isConnected: boolean;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

const WS_BASE = import.meta.env.VITE_WS_URL ?? '';
const MAX_BACKOFF_MS = 30_000;
const PING_INTERVAL_MS = 30_000;

function buildWsUrl(token: string): string {
  const base = WS_BASE || `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}`;
  return `${base}/ws?token=${encodeURIComponent(token)}`;
}

function jitter(ms: number): number {
  return ms + Math.floor(Math.random() * (ms * 0.5));
}

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const { token, isAuthenticated } = useAuth();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const positionListenersRef = useRef<Map<number, PositionCallback>>(new Map());
  const alertListenersRef = useRef<Set<AlertCallback>>(new Set());
  const isConnectedRef = useRef(false);
  const [isConnected, setIsConnected] = React.useState(false);

  const stopPing = useCallback(() => {
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
  }, []);

  const startPing = useCallback((ws: WebSocket) => {
    stopPing();
    pingTimerRef.current = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, PING_INTERVAL_MS);
  }, [stopPing]);

  const connect = useCallback(
    (currentToken: string) => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      const ws = new WebSocket(buildWsUrl(currentToken));
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptRef.current = 0;
        isConnectedRef.current = true;
        setIsConnected(true);
        startPing(ws);
      };

      ws.onmessage = (event: MessageEvent<string>) => {
        try {
          const msg = JSON.parse(event.data) as WebSocketMessage;
          if (msg.type === 'position') {
            const listener = positionListenersRef.current.get(msg.payload.deviceId);
            listener?.(msg.payload);
          } else if (msg.type === 'alert') {
            alertListenersRef.current.forEach((cb) => cb(msg.payload));
          }
        } catch {
          // ignore malformed messages
        }
      };

      ws.onerror = () => {
        // errors are handled by onclose
      };

      ws.onclose = () => {
        stopPing();
        isConnectedRef.current = false;
        setIsConnected(false);
        wsRef.current = null;

        // Exponential backoff with jitter: 1s, 2s, 4s, 8s ... capped at 30s
        const attempt = reconnectAttemptRef.current;
        const backoff = Math.min(1000 * Math.pow(2, attempt), MAX_BACKOFF_MS);
        const delay = jitter(backoff);
        reconnectAttemptRef.current = attempt + 1;

        reconnectTimerRef.current = setTimeout(() => {
          connect(currentToken);
        }, delay);
      };
    },
    [startPing, stopPing],
  );

  useEffect(() => {
    if (!isAuthenticated || !token) {
      wsRef.current?.close();
      return;
    }

    connect(token);

    return () => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      stopPing();
      wsRef.current?.close();
    };
  }, [isAuthenticated, token, connect, stopPing]);

  const subscribePosition = useCallback((deviceId: number, cb: PositionCallback) => {
    positionListenersRef.current.set(deviceId, cb);
  }, []);

  const unsubscribePosition = useCallback((deviceId: number) => {
    positionListenersRef.current.delete(deviceId);
  }, []);

  const subscribeAlerts = useCallback((cb: AlertCallback) => {
    alertListenersRef.current.add(cb);
  }, []);

  const unsubscribeAlerts = useCallback((cb?: AlertCallback) => {
    if (cb) {
      alertListenersRef.current.delete(cb);
    } else {
      alertListenersRef.current.clear();
    }
  }, []);

  return (
    <WebSocketContext.Provider
      value={{
        subscribePosition,
        unsubscribePosition,
        subscribeAlerts,
        unsubscribeAlerts,
        isConnected,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket(): WebSocketContextValue {
  const ctx = useContext(WebSocketContext);
  if (!ctx) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return ctx;
}
